import { useCallback, useEffect, useState } from 'react';
import { GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useSimulation } from '../../hooks/useSimulation';
import { useMapLayer } from '../../hooks/useMapLayer';
import { PORTUGAL_BOUNDS, type SimulationRequest } from '../../types';
import MapShell from '../map/MapShell';
import MapClickHandler from '../map/MapClickHandler';
import LayerControl from '../map/LayerControl';
import Spinner from '../ui/Spinner';

const INPUT = 'w-full px-3 py-2 text-sm rounded-lg bg-ink-950 border border-ink-700 text-mist placeholder:text-mist-faint focus:border-ember focus:ring-1 focus:ring-ember/40 outline-none transition-colors';
const LABEL = 'block text-xs font-medium text-mist-muted mb-1';

function IgnitionMarker({ lat, lon }: { lat: number; lon: number }) {
  const map = useMap();
  useEffect(() => {
    const icon = L.divIcon({
      html: `<div style="width:20px;height:20px;border-radius:50%;background:#F26A4B;border:3px solid #0B1210;box-shadow:0 2px 10px rgba(242,106,75,0.6)"></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
      className: '',
    });
    const marker = L.marker([lat, lon], { icon }).addTo(map);
    return () => {
      map.removeLayer(marker);
    };
  }, [map, lat, lon]);
  return null;
}

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-ink-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-3 py-2 text-sm font-medium text-mist bg-ink-800 flex items-center justify-between hover:bg-ink-700 transition-colors"
      >
        {title}
        <svg
          className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="p-3 space-y-3">{children}</div>}
    </div>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}) {
  return (
    <div>
      <label className={LABEL}>
        {label} {unit && <span className="text-mist-faint">({unit})</span>}
      </label>
      <input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        min={min}
        max={max}
        step={step}
        className={INPUT}
      />
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm text-mist-muted">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition-colors ${checked ? 'bg-ember' : 'bg-ink-700'}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-mist rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`}
        />
      </button>
    </label>
  );
}

export default function Simulator() {
  const { layer: mapLayer, setLayer: setMapLayer, config: layerConfig } = useMapLayer();
  const { mutate, data: result, isPending, error, reset } = useSimulation();

  const [ignition, setIgnition] = useState<{ lat: number; lon: number } | null>(null);

  const [useRealtimeWeather, setUseRealtimeWeather] = useState(true);
  const [windSpeed, setWindSpeed] = useState<number | null>(null);
  const [windDirection, setWindDirection] = useState<number | null>(null);
  const [temperature, setTemperature] = useState<number | null>(null);
  const [humidity, setHumidity] = useState<number | null>(null);

  const [ffmc, setFfmc] = useState<number | null>(null);
  const [dc, setDc] = useState<number | null>(null);
  const [bui, setBui] = useState<number | null>(null);
  const [isi, setIsi] = useState<number | null>(null);
  const [fwi, setFwi] = useState<number | null>(null);

  const [maxHours, setMaxHours] = useState(24);
  const [resolution, setResolution] = useState(1000);
  const [enableSpotting, setEnableSpotting] = useState(true);
  const [enableSuppression, setEnableSuppression] = useState(true);
  const [useDiurnalCycle, setUseDiurnalCycle] = useState(false);
  const [firmsAutoDetect, setFirmsAutoDetect] = useState(false);

  const handleMapClick = useCallback(
    (lat: number, lon: number) => {
      if (
        lat >= PORTUGAL_BOUNDS.south &&
        lat <= PORTUGAL_BOUNDS.north &&
        lon >= PORTUGAL_BOUNDS.west &&
        lon <= PORTUGAL_BOUNDS.east
      ) {
        setIgnition({ lat, lon });
        reset();
      }
    },
    [reset],
  );

  const handleRun = () => {
    if (!ignition) return;
    const request: SimulationRequest = {
      ignition_lat: ignition.lat,
      ignition_lon: ignition.lon,
      max_hours: maxHours,
      resolution_m: resolution,
      enable_spotting: enableSpotting,
      enable_suppression: enableSuppression,
      use_realtime_weather: useRealtimeWeather,
      use_diurnal_cycle: useDiurnalCycle,
      firms_auto_detect: firmsAutoDetect,
    };
    if (!useRealtimeWeather) {
      request.wind_speed_ms = windSpeed;
      request.wind_direction_deg = windDirection;
      request.temperature_c = temperature;
      request.humidity_pct = humidity;
      request.ffmc = ffmc;
      request.dc = dc;
      request.bui = bui;
      request.isi = isi;
      request.fwi = fwi;
    }
    mutate(request);
  };

  const geojsonStyle = { color: '#F26A4B', weight: 2, fillColor: '#F26A4B', fillOpacity: 0.4 };

  return (
    <div className="h-[calc(100vh-4rem)] flex bg-ink-950">
      {/* Sidebar */}
      <div className="w-96 flex-shrink-0 bg-ink-900 border-r border-ink-700 p-4 overflow-y-auto">
        <div className="mb-6">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-ember">IGNIS · Simulação</div>
          <h2 className="mt-1 font-display text-lg font-bold text-mist">Modelar propagação</h2>
        </div>

        <div className="space-y-4">
          {/* Ignition */}
          <div className="bg-ink-800 border border-ink-700 rounded-xl p-3">
            <h3 className="text-sm font-semibold text-mist mb-2">Ponto de ignição</h3>
            {ignition ? (
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-mist-muted">Lat:</span>
                  <span className="font-mono text-mist">{ignition.lat.toFixed(5)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-mist-muted">Lon:</span>
                  <span className="font-mono text-mist">{ignition.lon.toFixed(5)}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-mist-muted">Clique no mapa para definir o ponto de ignição.</p>
            )}
          </div>

          <Toggle label="Meteorologia em tempo real" checked={useRealtimeWeather} onChange={setUseRealtimeWeather} />

          {!useRealtimeWeather && (
            <CollapsibleSection title="Meteorologia" defaultOpen>
              <div className="grid grid-cols-2 gap-2">
                <NumberInput label="Vento" value={windSpeed} onChange={setWindSpeed} min={0} max={50} step={0.5} unit="m/s" />
                <NumberInput label="Dir. Vento" value={windDirection} onChange={setWindDirection} min={0} max={360} step={5} unit="graus" />
                <NumberInput label="Temperatura" value={temperature} onChange={setTemperature} min={-10} max={55} step={0.5} unit="°C" />
                <NumberInput label="Humidade" value={humidity} onChange={setHumidity} min={0} max={100} step={1} unit="%" />
              </div>
            </CollapsibleSection>
          )}

          {!useRealtimeWeather && (
            <CollapsibleSection title="FWI (Fire Weather Index)">
              <div className="grid grid-cols-2 gap-2">
                <NumberInput label="FFMC" value={ffmc} onChange={setFfmc} min={0} max={101} step={0.1} />
                <NumberInput label="DC" value={dc} onChange={setDc} min={0} max={800} step={1} />
                <NumberInput label="BUI" value={bui} onChange={setBui} min={0} max={300} step={1} />
                <NumberInput label="ISI" value={isi} onChange={setIsi} min={0} max={60} step={0.1} />
                <NumberInput label="FWI" value={fwi} onChange={setFwi} min={0} max={100} step={0.1} />
              </div>
            </CollapsibleSection>
          )}

          <CollapsibleSection title="Opções">
            <div>
              <label className={LABEL}>Duração máxima (horas)</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={168}
                  value={maxHours}
                  onChange={(e) => setMaxHours(Number(e.target.value))}
                  className="flex-1 accent-ember"
                />
                <span className="text-sm font-mono text-mist w-10 text-right">{maxHours}</span>
              </div>
            </div>

            <div>
              <label className={LABEL}>Resolução</label>
              <div className="flex gap-2">
                {[1000, 250].map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setResolution(r)}
                    className={`flex-1 py-1.5 text-sm rounded-lg border transition-colors ${
                      resolution === r
                        ? 'bg-ember text-ink-950 border-ember font-semibold'
                        : 'border-ink-700 text-mist-muted hover:border-ember/40'
                    }`}
                  >
                    {r}m
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2 pt-1">
              <Toggle label="Spotting" checked={enableSpotting} onChange={setEnableSpotting} />
              <Toggle label="Supressão" checked={enableSuppression} onChange={setEnableSuppression} />
              <Toggle label="Ciclo diurno" checked={useDiurnalCycle} onChange={setUseDiurnalCycle} />
              <Toggle label="FIRMS auto-detetar" checked={firmsAutoDetect} onChange={setFirmsAutoDetect} />
            </div>
          </CollapsibleSection>

          <button
            type="button"
            onClick={handleRun}
            disabled={!ignition || isPending}
            className="w-full py-3 rounded-xl font-semibold text-ink-950 transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-ember hover:brightness-110 shadow-[0_0_24px_-10px_#F26A4B]"
          >
            {isPending ? (
              <span className="flex items-center justify-center gap-2">
                <Spinner size="sm" />
                A simular...
              </span>
            ) : (
              'Executar simulação'
            )}
          </button>

          {error && (
            <div className="rounded-xl bg-risk-high/10 border border-risk-high/30 text-risk-high text-sm p-3">
              {(error as Error).message || 'Erro na simulação'}
            </div>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        <MapShell tileUrl={layerConfig.url} tileAttribution={layerConfig.attribution} tileKey={mapLayer} zoomControl={false}>
          <MapClickHandler onMapClick={handleMapClick} />
          {ignition && <IgnitionMarker lat={ignition.lat} lon={ignition.lon} />}
          {result?.burned_geojson && (
            <GeoJSON
              key={JSON.stringify(result.burned_geojson).slice(0, 100)}
              data={result.burned_geojson}
              style={() => geojsonStyle}
            />
          )}
        </MapShell>

        <div className="absolute top-4 left-4 z-[1000]">
          <LayerControl currentLayer={mapLayer} onLayerChange={setMapLayer} />
        </div>

        {result && (
          <div className="absolute bottom-4 left-4 right-4 z-[1000]">
            <div className="bg-ink-900/95 backdrop-blur border border-ink-700 rounded-xl px-6 py-4">
              <div className="flex items-center gap-6 flex-wrap">
                <div>
                  <div className="text-2xl font-bold text-ember">{result.burned_area_ha.toFixed(1)} ha</div>
                  <div className="text-xs text-mist-muted">Área ardida</div>
                </div>
                <div className="h-10 w-px bg-ink-700" />
                <div className="flex gap-6 text-sm">
                  <div><span className="text-mist-muted">Células: </span><span className="font-semibold text-mist">{result.burned_cells_count}</span></div>
                  <div><span className="text-mist-muted">Spot fires: </span><span className="font-semibold text-mist">{result.spot_fires_count}</span></div>
                  <div><span className="text-mist-muted">Passos: </span><span className="font-semibold text-mist">{result.timesteps_run}</span></div>
                  <div><span className="text-mist-muted">Tempo: </span><span className="font-semibold text-mist">{(result.elapsed_ms / 1000).toFixed(2)}s</span></div>
                  <div><span className="text-mist-muted">Meteo: </span><span className="font-semibold text-mist">{result.weather_source}</span></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
